import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Render the markdown an LLM writes as HTML.
 *
 * The studio's replies come back as markdown — `**bold**`, numbered steps, fenced
 * code, the occasional table — and interpolating them into the bubble showed the
 * syntax instead of the formatting.
 *
 * SAFETY. This is model output, and some of it quotes text a user supplied, so it
 * is not trusted. The renderer is safe by construction rather than by filtering:
 *
 *   1. every angle bracket, ampersand and quote in the source is escaped FIRST,
 *      so no tag in the input can survive into the output;
 *   2. the only tags in the result are the ones this file writes;
 *   3. link and image URLs are the one place a value from the input reaches an
 *      attribute, and those are checked against a protocol allowlist — which is
 *      what stops `[x](javascript:...)`.
 *
 * That ordering is why `bypassSecurityTrustHtml` is defensible here: nothing
 * reaches it that did not come out of this file's own emitters.
 *
 * Not a full CommonMark implementation, and deliberately so — a parser for the
 * whole spec is a dependency, not a pipe. It covers what the studio actually
 * emits; anything it does not recognise falls through as text, which is the same
 * thing the reader saw before.
 */
@Pipe({
    name: 'markdown',
    pure: true
})
export class MarkdownPipe implements PipeTransform {

    /** Schemes a link or image may use. Everything else is dropped. */
    private static readonly SAFE_PROTOCOL = /^(https?:\/\/|mailto:|tel:|\/|#)/i;

    /**
     * Stand-ins for code that is lifted out while the rest is rendered.
     *
     * Delimited with NUL rather than spelled as a word: a placeholder like `CODE0`
     * would be substituted out of a reply that happened to mention CODE0 in prose,
     * which is not hypothetical in a studio whose service codes get quoted back.
     * NUL cannot appear in the text the model sends, and no rule below touches it.
     */
    private static readonly NUL = '\u0000';

    constructor(private sanitizer: DomSanitizer) { }

    transform(value: string | null | undefined): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(this.render(value));
    }

    private render(value: string | null | undefined): string {
        const source = (value === null || value === undefined) ? '' : String(value);
        if (!source.trim()) { return ''; }

        const nul = MarkdownPipe.NUL;

        /*
         * Fenced code is lifted out before anything else touches the text, and put
         * back at the very end. Its contents are markup to the reader, not to the
         * renderer: `**` inside a snippet has to stay `**`.
         */
        const fences: string[] = [];
        const liftFence = (lang: string, body: string): string => {
            const cls = lang
                ? ' class="md-code md-code--' + this.escape(lang) + '"'
                : ' class="md-code"';
            fences.push('<pre' + cls + '><code>'
                + this.escape(body.replace(/\s+$/, '')) + '</code></pre>');
            // Put on its own line so the block pass sees it as a block, not as
            // part of whatever paragraph surrounded the fence.
            return '\n' + nul + 'f' + (fences.length - 1) + nul + '\n';
        };

        let text = source.replace(/\r\n?/g, '\n');

        text = text.replace(/```([\w+-]*)\n([\s\S]*?)```/g,
            (_m, lang: string, body: string) => liftFence(lang, body));

        // A fence the model never closed — everything after it is still code.
        text = text.replace(/```([\w+-]*)\n([\s\S]*)$/,
            (_m, lang: string, body: string) => liftFence(lang, body));

        const html = this.blocks(text);

        return html.replace(
            new RegExp(nul + 'f(\\d+)' + nul, 'g'),
            (_m, i: string) => fences[Number(i)] || ''
        );
    }

    /* ---------- Block level ---------- */

    /**
     * Walk the lines once, opening and closing blocks as their markers appear.
     *
     * A line-driven pass rather than a set of whole-document regexes: a list needs
     * to know where it ends, and a regex spanning lines cannot tell a list item
     * from a paragraph that happens to start with a digit.
     */
    private blocks(text: string): string {
        const nul = MarkdownPipe.NUL;
        const fenceLine = new RegExp('^' + nul + 'f\\d+' + nul + '$');

        const lines = text.split('\n');
        const out: string[] = [];

        // The list currently open, if any, and the paragraph being gathered.
        let listTag: 'ul' | 'ol' | null = null;
        let paragraph: string[] = [];
        let quote: string[] = [];

        const closeParagraph = () => {
            if (!paragraph.length) { return; }
            out.push('<p>' + this.inline(paragraph.join('\n')) + '</p>');
            paragraph = [];
        };

        const closeList = () => {
            if (!listTag) { return; }
            out.push('</' + listTag + '>');
            listTag = null;
        };

        const closeQuote = () => {
            if (!quote.length) { return; }
            out.push('<blockquote>' + this.blocks(quote.join('\n')) + '</blockquote>');
            quote = [];
        };

        const closeAll = () => { closeParagraph(); closeList(); closeQuote(); };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // A lifted code fence stands alone — it is already HTML.
            if (fenceLine.test(line)) {
                closeAll();
                out.push(line);
                continue;
            }

            if (!line.trim()) {
                // A blank line ends a paragraph and a quote, but not a list:
                // markdown allows a blank line between items of one loose list.
                closeParagraph();
                closeQuote();
                continue;
            }

            const quoted = line.match(/^\s{0,3}>\s?(.*)$/);
            if (quoted) {
                closeParagraph();
                closeList();
                quote.push(quoted[1]);
                continue;
            }
            closeQuote();

            // Thematic break, tested before the list rule: `---` is not a bullet.
            if (/^\s{0,3}([-*_])(\s*\1){2,}\s*$/.test(line)) {
                closeAll();
                out.push('<hr />');
                continue;
            }

            const heading = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
            if (heading) {
                closeAll();
                const level = heading[1].length;
                out.push('<h' + level + '>' + this.inline(heading[2].trim()) + '</h' + level + '>');
                continue;
            }

            // A table is only a table with its separator row, so it is matched on
            // the pair and then consumes as many body rows as follow.
            if (this.looksLikeTableHead(lines, i)) {
                closeAll();
                const consumed = this.table(lines, i);
                out.push(consumed.html);
                i = consumed.next - 1;
                continue;
            }

            const item = line.match(/^\s*(?:([-*+])|(\d{1,9})[.)])\s+(.*)$/);
            if (item) {
                closeParagraph();
                const tag: 'ul' | 'ol' = item[1] ? 'ul' : 'ol';

                if (listTag && listTag !== tag) { closeList(); }

                if (!listTag) {
                    listTag = tag;
                    /*
                     * An ordered list that does not start at 1 keeps its own
                     * numbering — the model writes "3." when it means the third
                     * step, and renumbering from 1 would misstate the instructions.
                     */
                    const start = tag === 'ol' ? parseInt(item[2], 10) : 1;
                    out.push(tag === 'ol' && start !== 1
                        ? '<ol start="' + start + '">'
                        : '<' + tag + '>');
                }

                out.push('<li>' + this.inline(item[3]) + '</li>');
                continue;
            }

            /*
             * An indented line under an open list continues that item rather than
             * starting a paragraph — this is the wrapped second line of a step.
             */
            if (listTag && /^\s{2,}\S/.test(line)) {
                const last = out.length - 1;
                if (out[last] && out[last].slice(-5) === '</li>') {
                    out[last] = out[last].slice(0, -5) + ' ' + this.inline(line.trim()) + '</li>';
                    continue;
                }
            }

            closeList();
            paragraph.push(line);
        }

        closeAll();
        return out.join('');
    }

    /** A header row followed by a `|---|---|` separator. */
    private looksLikeTableHead(lines: string[], i: number): boolean {
        const head = lines[i] || '';
        const rule = lines[i + 1] || '';
        return head.indexOf('|') > -1
            && /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(rule);
    }

    private table(lines: string[], i: number): { html: string; next: number } {
        const cells = (row: string) => row
            .replace(/^\s*\|/, '')
            .replace(/\|\s*$/, '')
            .split('|')
            .map(c => this.inline(c.trim()));

        const head = cells(lines[i]);
        let row = i + 2;
        const body: string[][] = [];

        while (row < lines.length && lines[row].indexOf('|') > -1 && lines[row].trim()) {
            body.push(cells(lines[row]));
            row++;
        }

        const th = head.map(c => '<th>' + c + '</th>').join('');
        const tr = body
            .map(r => '<tr>' + r.map(c => '<td>' + c + '</td>').join('') + '</tr>')
            .join('');

        // Wrapped so a wide table scrolls inside the bubble instead of stretching it.
        return {
            html: '<div class="md-table-wrap"><table class="md-table"><thead><tr>'
                + th + '</tr></thead><tbody>' + tr + '</tbody></table></div>',
            next: row
        };
    }

    /* ---------- Inline level ---------- */

    /**
     * Escape first, then apply the inline markers.
     *
     * Inline code comes out before the emphasis rules for the same reason fences
     * do: `*` inside a code span is a literal asterisk.
     */
    private inline(text: string): string {
        const nul = MarkdownPipe.NUL;
        const codes: string[] = [];

        let s = this.escape(text).replace(/(`+)([\s\S]*?)\1/g, (_m, _ticks, body: string) => {
            codes.push('<code class="md-inline-code">' + body.trim() + '</code>');
            return nul + 'c' + (codes.length - 1) + nul;
        });

        // Images before links — the two syntaxes differ by one leading character.
        s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;[^)]*&quot;)?\)/g,
            (m, alt: string, url: string) => this.isSafeUrl(url)
                ? '<img class="md-image" src="' + url + '" alt="' + alt + '" />'
                : m);

        s = s.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+&quot;[^)]*&quot;)?\)/g,
            (m, label: string, url: string) => this.isSafeUrl(url)
                // noopener because target=_blank hands the opener to the new page.
                ? '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + label + '</a>'
                : m);

        // Bold before italic: `***x***` and `**x**` must not be eaten one star at
        // a time by the single-marker rule.
        s = s.replace(/\*\*\*([^\s*][\s\S]*?)\*\*\*/g, '<strong><em>$1</em></strong>')
            .replace(/\*\*([^\s*][\s\S]*?)\*\*/g, '<strong>$1</strong>')
            .replace(/(^|[^\w*])\*([^\s*][\s\S]*?)\*(?![\w*])/g, '$1<em>$2</em>')
            .replace(/~~([\s\S]+?)~~/g, '<del>$1</del>');

        /*
         * `_` is emphasis only at a word boundary. Inside a word it is ordinary
         * punctuation, and treating it otherwise mangles the identifiers the studio
         * writes constantly — service codes like image_nano_banana, column names.
         */
        s = s.replace(/(^|[^\w_])__([^\s_][\s\S]*?)__(?![\w_])/g, '$1<strong>$2</strong>')
            .replace(/(^|[^\w_])_([^\s_][\s\S]*?)_(?![\w_])/g, '$1<em>$2</em>');

        // A single newline inside a paragraph is a visible break: the model uses it
        // to lay out short lines it did not mark up as a list.
        s = s.replace(/\n/g, '<br />');

        return s.replace(
            new RegExp(nul + 'c(\\d+)' + nul, 'g'),
            (_m, i: string) => codes[Number(i)] || ''
        );
    }

    /** The one gate on a value from the input reaching an attribute. */
    private isSafeUrl(url: string): boolean {
        return MarkdownPipe.SAFE_PROTOCOL.test((url || '').trim());
    }

    private escape(text: string): string {
        return (text || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
